import { db } from '../firebase';
import {
  doc,
  getDoc,
  getDocs,
  updateDoc,
  setDoc,
  addDoc,
  collection,
  writeBatch,
  arrayUnion,
  serverTimestamp,
} from 'firebase/firestore';
import { multiFamilyService } from './multiFamilyService';
import logger from '../utils/logger';

/**
 * Service for merging two family accounts into one.
 * Used by family admins when two separate family accounts need to be combined
 * (e.g., after a marriage, or when duplicate families were accidentally created).
 *
 * Merge strategy:
 *   - Target family (primary) keeps its familyCode, settings, and groups
 *   - Source family (secondary) members are migrated into the target
 *   - Source family's documents/photos are re-parented under target
 *   - Source family is marked as merged/inactive
 */
class FamilyMergeService {
  /**
   * Preview what a merge would look like without executing it.
   * @param {string} sourceFamilyCode - Family to merge FROM (will be absorbed)
   * @param {string} targetFamilyCode - Family to merge INTO (primary)
   * @param {string} callerUserId - User requesting the preview (must be admin of at least one family)
   * @returns {Promise<Object>} Merge preview with member counts, conflicts, etc.
   */
  async previewMerge(sourceFamilyCode, targetFamilyCode, callerUserId) {
    if (!sourceFamilyCode || !targetFamilyCode) {
      throw new Error('Both source and target family codes are required');
    }
    if (!callerUserId) {
      throw new Error('Caller user ID is required');
    }
    if (sourceFamilyCode === targetFamilyCode) {
      throw new Error('Cannot merge a family with itself');
    }

    // Verify caller is an admin of at least one of the families
    const [sourceMemberDoc, targetMemberDoc] = await Promise.all([
      getDoc(doc(db, 'families', sourceFamilyCode, 'members', callerUserId)),
      getDoc(doc(db, 'families', targetFamilyCode, 'members', callerUserId))
    ]);
    const isSourceAdmin = sourceMemberDoc.exists() && (sourceMemberDoc.data().groups || []).includes('ADMIN');
    const isTargetAdmin = targetMemberDoc.exists() && (targetMemberDoc.data().groups || []).includes('ADMIN');
    if (!isSourceAdmin && !isTargetAdmin) {
      throw new Error('You must be an admin of at least one of the families to preview a merge');
    }

    const [sourceFamily, targetFamily] = await Promise.all([
      getDoc(doc(db, 'families', sourceFamilyCode)),
      getDoc(doc(db, 'families', targetFamilyCode))
    ]);

    if (!sourceFamily.exists()) throw new Error('Source family not found');
    if (!targetFamily.exists()) throw new Error('Target family not found');

    const sourceData = sourceFamily.data();
    const targetData = targetFamily.data();

    // Get members from both families
    const [sourceMembers, targetMembers] = await Promise.all([
      getDocs(collection(db, 'families', sourceFamilyCode, 'members')),
      getDocs(collection(db, 'families', targetFamilyCode, 'members'))
    ]);

    const sourceMemberList = sourceMembers.docs.map(d => ({ id: d.id, ...d.data() }));
    const targetMemberList = targetMembers.docs.map(d => ({ id: d.id, ...d.data() }));

    // Find overlapping members (same user in both families)
    const targetMemberIds = new Set(targetMemberList.map(m => m.id));
    const overlapping = sourceMemberList.filter(m => targetMemberIds.has(m.id));
    const newMembers = sourceMemberList.filter(m => !targetMemberIds.has(m.id));

    // Get groups from both families
    const [sourceGroups, targetGroups] = await Promise.all([
      getDocs(collection(db, 'families', sourceFamilyCode, 'groups')),
      getDocs(collection(db, 'families', targetFamilyCode, 'groups'))
    ]);

    return {
      source: {
        familyCode: sourceFamilyCode,
        name: sourceData.name,
        memberCount: sourceData.memberCount || sourceMemberList.length,
        members: sourceMemberList,
        groupCount: sourceGroups.size
      },
      target: {
        familyCode: targetFamilyCode,
        name: targetData.name,
        memberCount: targetData.memberCount || targetMemberList.length,
        members: targetMemberList,
        groupCount: targetGroups.size
      },
      merge: {
        newMembersToAdd: newMembers.length,
        overlappingMembers: overlapping.length,
        overlappingMemberDetails: overlapping.map(m => ({
          id: m.id,
          name: m.displayName || m.email,
          email: m.email
        })),
        newMembers: newMembers.map(m => ({
          id: m.id,
          name: m.displayName || m.email,
          email: m.email,
          role: m.role,
          relationship: m.relationship
        })),
        totalMembersAfterMerge: targetMemberList.length + newMembers.length
      },
      callerIsTargetAdmin: isTargetAdmin,
      callerIsSourceAdmin: isSourceAdmin
    };
  }

  /**
   * Request a merge from a source family admin to target family admin.
   * Used when the caller is admin of source but NOT target — target admin must approve.
   * @param {string} sourceFamilyCode - Family to merge FROM
   * @param {string} targetFamilyCode - Family to merge INTO
   * @param {string} adminUserId - Admin of source family requesting the merge
   * @param {string} adminEmail - Email of the requesting admin
   * @param {string} adminName - Display name of the requesting admin
   * @returns {Promise<string>} Request ID
   */
  async requestMerge(sourceFamilyCode, targetFamilyCode, adminUserId, adminEmail, adminName) {
    if (!sourceFamilyCode || !targetFamilyCode || !adminUserId) {
      throw new Error('Source family, target family, and admin user ID are required');
    }

    // Verify caller is admin of the source family
    const sourceAdmin = await getDoc(doc(db, 'families', sourceFamilyCode, 'members', adminUserId));
    const isSourceAdmin = sourceAdmin.exists() && (sourceAdmin.data().groups || []).includes('ADMIN');
    if (!isSourceAdmin) {
      throw new Error('You must be an admin of the source family to request a merge');
    }

    // Check for existing pending merge request
    const existingQuery = collection(db, 'families', targetFamilyCode, 'accessRequests');
    const existingSnap = await getDocs(existingQuery);
    const alreadyPending = existingSnap.docs.some(d => {
      const data = d.data();
      return data.type === 'family_merge' && data.sourceFamilyCode === sourceFamilyCode && data.status === 'pending';
    });
    if (alreadyPending) {
      throw new Error('A merge request for this family is already pending approval.');
    }

    const sourceFamily = await getDoc(doc(db, 'families', sourceFamilyCode));
    const targetFamily = await getDoc(doc(db, 'families', targetFamilyCode));
    if (!sourceFamily.exists()) throw new Error('Source family not found');
    if (!targetFamily.exists()) throw new Error('Target family not found');

    const requestRef = await addDoc(collection(db, 'families', targetFamilyCode, 'accessRequests'), {
      type: 'family_merge',
      status: 'pending',
      sourceFamilyCode,
      targetFamilyCode,
      sourceFamilyName: sourceFamily.data().name || sourceFamilyCode,
      targetFamilyName: targetFamily.data().name || targetFamilyCode,
      requestedBy: adminUserId,
      requestedByEmail: adminEmail,
      requestedByName: adminName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    return requestRef.id;
  }

  /**
   * Execute the merge of source family into target family.
   * @param {string} sourceFamilyCode - Family to merge FROM
   * @param {string} targetFamilyCode - Family to merge INTO
   * @param {string} adminUserId - Admin performing the merge
   * @param {Object} options
   * @param {string} options.defaultGroup - Group to assign new members to (default: 'EXTENDED_FAMILY')
   * @returns {Promise<Object>} Merge result summary
   */
  async executeMerge(sourceFamilyCode, targetFamilyCode, adminUserId, options = {}) {
    if (!sourceFamilyCode || !targetFamilyCode || !adminUserId) {
      throw new Error('Source family, target family, and admin user ID are required');
    }

    const defaultGroup = options.defaultGroup || 'EXTENDED_FAMILY';

    // Verify admin has permission on both families
    const [, targetAdmin] = await Promise.all([
      getDoc(doc(db, 'families', sourceFamilyCode, 'members', adminUserId)),
      getDoc(doc(db, 'families', targetFamilyCode, 'members', adminUserId))
    ]);

    const isTargetAdmin = targetAdmin.exists() && (targetAdmin.data().groups || []).includes('ADMIN');

    if (!isTargetAdmin) {
      throw new Error('You must be an admin of the target (destination) family to execute a merge. Use "Request Merge" instead.');
    }

    // Get preview data
    const preview = await this.previewMerge(sourceFamilyCode, targetFamilyCode);
    const { newMembers } = preview.merge;

    let migratedCount = 0;
    let skippedCount = preview.merge.overlappingMembers;

    // Migrate new members in batches (Firestore batch limit is 500)
    const batchSize = 250; // Leave room for group updates
    for (let i = 0; i < newMembers.length; i += batchSize) {
      const batch = writeBatch(db);
      const chunk = newMembers.slice(i, i + batchSize);

      for (const member of chunk) {
        // Get full member data from source
        const sourceMemberDoc = await getDoc(
          doc(db, 'families', sourceFamilyCode, 'members', member.id)
        );
        if (!sourceMemberDoc.exists()) continue;

        const memberData = sourceMemberDoc.data();

        // Create member in target family
        const targetMemberRef = doc(db, 'families', targetFamilyCode, 'members', member.id);
        batch.set(targetMemberRef, {
          ...memberData,
          groups: [defaultGroup],
          role: 'member', // Reset to member in new family
          mergedFrom: sourceFamilyCode,
          mergedAt: new Date(),
          updatedAt: new Date()
        });

        // Update user's top-level document
        const userRef = doc(db, 'users', member.id);
        batch.update(userRef, {
          activeFamilyCode: targetFamilyCode,
          currentFamily: targetFamilyCode, // Legacy compat
          familyCode: targetFamilyCode, // Legacy compat
          families: arrayUnion(targetFamilyCode), // Legacy compat
          role: 'member',
          groups: [defaultGroup],
          isAdmin: false,
          updatedAt: new Date()
        });

        // Add to default group
        const groupRef = doc(db, 'families', targetFamilyCode, 'groups', defaultGroup);
        batch.update(groupRef, {
          members: arrayUnion(member.id),
          updatedAt: new Date()
        });

        migratedCount++;
      }

      await batch.commit();
    }

    // For overlapping members, add target family to their families array
    for (const member of preview.merge.overlappingMemberDetails) {
      try {
        await multiFamilyService.addFamilyToUser(member.id, targetFamilyCode);
      } catch (err) {
        logger.error(`Error updating overlapping member ${member.id}:`, err);
      }
    }

    // Update target family member count
    const targetFamilyRef = doc(db, 'families', targetFamilyCode);
    const targetFamilyDoc = await getDoc(targetFamilyRef);
    const currentCount = targetFamilyDoc.data().memberCount || 0;
    await updateDoc(targetFamilyRef, {
      memberCount: currentCount + migratedCount,
      updatedAt: new Date()
    });

    // Mark source family as merged/inactive
    const sourceFamilyRef = doc(db, 'families', sourceFamilyCode);
    await updateDoc(sourceFamilyRef, {
      isActive: false,
      mergedInto: targetFamilyCode,
      mergedBy: adminUserId,
      mergedAt: new Date(),
      updatedAt: new Date()
    });

    // Create audit record
    const auditRef = doc(collection(db, 'auditLogs'));
    await setDoc(auditRef, {
      type: 'family_merge',
      performedBy: adminUserId,
      sourceFamilyCode,
      targetFamilyCode,
      membersMigrated: migratedCount,
      membersSkipped: skippedCount,
      totalAfterMerge: currentCount + migratedCount,
      createdAt: new Date()
    });

    return {
      success: true,
      membersMigrated: migratedCount,
      membersSkipped: skippedCount,
      totalMembers: currentCount + migratedCount,
      sourceFamilyDeactivated: true
    };
  }
}

export const familyMergeService = new FamilyMergeService();
